import { DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UserApplications } from '@workspace/data-types';
import { environment } from '@workspace/ng-config';
import { ApiService } from '@workspace/ng-http';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { CookieService } from 'ngx-cookie-service';
import { catchError, EMPTY, mergeMap, Observable, switchMap, tap } from 'rxjs';
import { LocalStorageKey, Scheme, SessionStorageKey } from '../models';
import { ApplicationStore, AuthStore } from '../ngrx';
import { SessionSyncService } from './session-sync.service';
import { UserRegistrationAuthService } from './user-registration-auth.service';

@Service()
export class OpenIdService {
  private authStore = inject(AuthStore);
  private appStore = inject(ApplicationStore);
  private oidc = inject(OidcSecurityService);
  private userRegistrationAuthService = inject(UserRegistrationAuthService);
  private router = inject(Router);
  private api = inject(ApiService);
  private destroyRef = inject(DestroyRef);
  private cookieService = inject(CookieService);
  private sessionSync = inject(SessionSyncService);

  private isPublicApp(): boolean {
    const currentApp = this.appStore.currentApplicationName();
    if (currentApp) {
      return currentApp === UserApplications.PUBLIC;
    }
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      const hostname = window.location.hostname;
      if (hostname.startsWith('admin.')) return false;
      if (pathname.startsWith('/owner') || pathname.startsWith('/agent')) {
        return false;
      }
      return true;
    }
    return false;
  }

  private isPublicAppOnly(): boolean {
    const currentApp = this.appStore.currentApplicationName();
    if (currentApp) {
      return currentApp === UserApplications.PUBLIC;
    }
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      const hostname = window.location.hostname;
      if (hostname.startsWith('admin.')) return false;
      if (pathname.startsWith('/owner') || pathname.startsWith('/agent')) {
        return false;
      }
      return true;
    }
    return false;
  }

  isAuthenticated = false;
  initialize(): Observable<void> {
    return this.oidc.checkAuth().pipe(
      switchMap((openId) => {
        this.isAuthenticated = openId.isAuthenticated;
        if (openId.isAuthenticated) {
          sessionStorage.removeItem(SessionStorageKey.SILENT_CHECK_ATTEMPTED);
          this.sessionSync.notifySessionUpdate();
          this.restartSync();
          return this.api
            .userSessions()
            .start()
            .pipe(switchMap(() => this.authStore.initAuth(openId)));
        }

        // On public app, auth is optional:
        // Only attempt silent check if an active session signal is detected on the domain (.etxe.dev)
        if (this.isPublicApp()) {
          const hasActiveSession = this.sessionSync.hasActiveSession();
          if (hasActiveSession && !sessionStorage.getItem(SessionStorageKey.SILENT_CHECK_ATTEMPTED)) {
            this.trySilentAuthorizeCheck();
            return EMPTY;
          }

          // If session check was attempted and user is unauthenticated, clear any stale session signal
          if (hasActiveSession) {
            this.sessionSync.clearSessionSignal();
          }

          this.waitForLoggin();
          this.restartSync();
          return this.initializeUserRegistration();
        }

        if (!sessionStorage.getItem(SessionStorageKey.SILENT_CHECK_ATTEMPTED)) {
          this.trySilentAuthorizeCheck();
          return EMPTY;
        }

        this.waitForLoggin();
        this.restartSync();
        return this.initializeUserRegistration();
      }),
    );
  }

  login(email?: string, returnUrl?: string) {
    const existingReturnUrl = localStorage.getItem(LocalStorageKey.AUTH_RETURN_URL);
    const isRegistrationOrAuthPath =
      window.location.pathname.startsWith('/landing/user-registration') ||
      window.location.pathname.startsWith('/auth');

    const targetUrl =
      returnUrl ||
      (!isRegistrationOrAuthPath ? window.location.pathname + window.location.search : existingReturnUrl);

    if (targetUrl && !targetUrl.includes('/auth/openid_connect/callback')) {
      localStorage.setItem(LocalStorageKey.AUTH_RETURN_URL, targetUrl);
    }
    const customParams = email
      ? {
          login_hint: email,
          dark: (this.cookieService.get(SessionStorageKey.SCHEME) as Scheme) === 'dark',
        }
      : undefined;

    this.oidc.authorize(undefined, {
      customParams,
      urlHandler: (url) => window.location.replace(url),
    });
  }

  logout(redirect = true) {
    this.api
      .user()
      .account()
      .logOut()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.wipeOut(redirect)),
      )
      .subscribe();
  }

  wipeOut(redirect = true) {
    this.sessionSync.clearSessionSignal();
    sessionStorage.removeItem(SessionStorageKey.SILENT_CHECK_ATTEMPTED);
    localStorage.removeItem(LocalStorageKey.AUTH_RETURN_URL);
    this.oidc.logoffLocal();
    this.authStore.logout();

    if (redirect) {
      if (this.isPublicAppOnly()) {
        void this.router.navigate(['/auth/sign-in']);
      } else {
        window.location.href = `${environment.apps.publicUrl}/auth/sign-in`;
      }
    }
  }

  restartSync() {
    this.sessionSync.stopMonitoring();

    this.sessionSync.startMonitoring(this.isAuthenticated, {
      // CASE: I am anonymous, but I logged in on another tab
      onSessionFound: () => {
        console.log('Login detected elsewhere! Logging in...');
        const currentPath = window.location.pathname + window.location.search;
        if (!currentPath.includes('/auth/openid_connect/callback')) {
          localStorage.setItem(LocalStorageKey.AUTH_RETURN_URL, currentPath);
        }
        if (this.isPublicApp()) {
          this.trySilentAuthorizeCheck();
          return;
        }
        this.oidc.authorize();
      },

      // CASE: I am logged in, but I updated my profile on another tab
      onSessionUpdate: () => {
        console.log('User data changed elsewhere. Refreshing...');
        // Force refresh user data from API/Keycloak
        this.oidc.checkAuth().subscribe();
        // OR: this.userProfileService.load().subscribe();
      },

      // CASE: I am logged in, but I logged out on another tab
      onSessionLost: () => {
        console.log('Logout detected.');
        this.wipeOut(!this.isPublicApp());
      },
    });
  }

  private trySilentAuthorizeCheck(): void {
    sessionStorage.setItem(SessionStorageKey.SILENT_CHECK_ATTEMPTED, 'true');
    const currentPath = window.location.pathname + window.location.search;
    if (!currentPath.includes('/auth/openid_connect/callback')) {
      const returnUrl = currentPath === '/' || currentPath === '' ? '/' : currentPath;
      localStorage.setItem(LocalStorageKey.AUTH_RETURN_URL, returnUrl);
    }
    this.oidc.authorize(undefined, {
      customParams: { prompt: 'none' },
      urlHandler: (url) => window.location.replace(url),
    });
  }

  private waitForLoggin(): void {
    this.authStore.failedAuth();
    if (
      typeof window !== 'undefined' &&
      !window.location.pathname.includes('/auth/openid_connect/callback')
    ) {
      localStorage.removeItem(LocalStorageKey.AUTH_RETURN_URL);
    }
  }

  getAccessToken(): Observable<string | null> {
    return this.oidc.getAccessToken();
  }

  private initializeUserRegistration() {
    return this.userRegistrationAuthService.executeTokenRefresh().pipe(
      mergeMap(() => this.authStore.initAuthRegistration()),
      catchError(() => {
        this.authStore.failedAuth();
        return EMPTY;
      }),
    );
  }
}
